'use client';

import { c as _c } from "react/compiler-runtime";
import { type Node } from '@react-types/shared';
import classNames from 'classnames';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { type AriaTabListProps, type AriaTabPanelProps, type AriaTabProps, useFocusVisible, useTab, useTabList, useTabPanel } from 'react-aria';
import Skeleton from 'react-loading-skeleton';
import { type TabListState, useTabListState } from 'react-stately';
import { Caret, SuccessIcon } from '../svgs';
import { TabPanelSkeleton, TabsNavigationSkeleton } from './components/hash-tabs-navigation-skeleton';
import styles from './hash-tabs-navigation.module.scss';
export type HashTabsNavigationItems = Iterable<TabItemProps> | undefined;
type TabItemProps = AriaTabProps & {
  formComponent: null | ReactNode;
  hashUrl: string;
  key: string;
  relativePath: null | string | undefined;
  tabTitle: string;
};
const Tab = t0 => {
  const $ = _c(15);
  const {
    item,
    state
  } = t0;
  const {
    key,
    value
  } = item;
  const {
    isFocusVisible
  } = useFocusVisible();
  const ref = useRef(null);
  let t1;
  if ($[0] !== key) {
    t1 = {
      key
    };
    $[0] = key;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    isSelected,
    tabProps
  } = useTab(t1, state, ref);
  let t2;
  if ($[2] !== isFocusVisible || $[3] !== isSelected) {
    t2 = classNames(styles["navigation-tab"], {
      [styles["navigation-tab--active"]]: isSelected,
      [styles["navigation-tab--focus-visible"]]: isFocusVisible
    });
    $[2] = isFocusVisible;
    $[3] = isSelected;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const t3 = value?.hashUrl;
  const t4 = value?.key;
  const t5 = value?.tabTitle;
  let t6;
  if ($[5] !== isSelected || $[6] !== value?.key) {
    t6 = isSelected && <SuccessIcon className={styles["navigation-tab__check-icon"]} key={value?.key} />;
    $[5] = isSelected;
    $[6] = value?.key;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t2 || $[9] !== t3 || $[10] !== t4 || $[11] !== t5 || $[12] !== t6 || $[13] !== tabProps) {
    t7 = <a className={t2} data-testid="tab-item" {...tabProps} href={t3} key={t4} ref={ref}>{t5}{t6}</a>;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
    $[13] = tabProps;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  return t7;
};
const TabPanel = t0 => {
  const $ = _c(8);
  let props;
  let state;
  if ($[0] !== t0) {
    ({
      state,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
    $[2] = state;
  } else {
    props = $[1];
    state = $[2];
  }
  const ref = useRef(null);
  const {
    tabPanelProps
  } = useTabPanel(props, state, ref);
  let t1;
  if ($[3] !== state.selectedItem) {
    t1 = state.selectedItem ? state.selectedItem.props.children : <TabPanelSkeleton />;
    $[3] = state.selectedItem;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== t1 || $[6] !== tabPanelProps) {
    t2 = <div className={styles["settings-section"]} data-testid="settings-tab-content" {...tabPanelProps} ref={ref} tabIndex={0}>{t1}</div>;
    $[5] = t1;
    $[6] = tabPanelProps;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
export const HashTabsNavigation = (props: AriaTabListProps<TabItemProps> & {
  initialTabType: string;
}) => {
  const [navigationExpanded, setNavigationExpanded] = useState(false);
  const [selectedTab, setSelectedTab] = useState('');
  const state = useTabListState({
    onSelectionChange: key => {
      window.location.hash = String(key);
      setSelectedTab(String(key));
      setNavigationExpanded(false);
      setTimeout(() => {
        window.scrollTo({
          behavior: 'smooth',
          top: 0
        });
      }, 0);
    },
    selectedKey: selectedTab ? selectedTab.replace('#', '') : '',
    ...props
  });
  const ref = useRef(null);
  const contentContainerRef = useRef<HTMLDivElement>(null);
  const {
    tabListProps
  } = useTabList(props, state, ref);
  const handleHashChange = () => {
    setSelectedTab(window.location.hash || props.initialTabType);
  };
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      setSelectedTab(hash);
    } else {
      setSelectedTab(props.initialTabType);
      window.location.hash = props.initialTabType;
    }
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return <div className={styles['navigation-tabs-wrapper']}>
      <div className={classNames(styles['navigation-tabs-container'], {
      [styles['navigation-tabs-container--expanded']]: navigationExpanded
    })} data-testid="navigation-tabs-container">
        {state?.selectedItem?.value?.tabTitle ? <button aria-expanded={navigationExpanded} className={styles['navigation-tabs-container__expand-button']} onClick={() => setNavigationExpanded(previous => !previous)}>
            <Caret className={styles['navigation-tabs-container__expand-button-caret']} />
            {state?.selectedItem?.value?.tabTitle}
          </button> : <div className={styles['navigation-tabs-container__expand-button--skeleton']}>
            <Skeleton height={47} />
          </div>}
        <div className={styles['navigation-tabs-container__tabs-list']} {...tabListProps} data-testid="navigation-tabs-list" ref={ref}>
          {state.selectedItem ? [...state.collection].map(item => {
          return <Tab item={item} key={item.key} state={state} />;
        }) : <TabsNavigationSkeleton />}
        </div>
      </div>
      <div className={styles['navigation-tabs-content']} ref={contentContainerRef}>
        <TabPanel key={state.selectedItem?.key} state={state} />
      </div>
    </div>;
};